import { ScanLine, Search, ShieldCheck, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  Field,
  Input,
  Select,
  useToast,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { useBranch } from '@/features/branch/branch-context';
import { usePayers } from '@/features/patients/api';
import {
  arrivalLookup,
  useQueueActions,
  useServices,
} from '@/features/queue/api';
import type {
  ArrivalLookup,
  PayerNotice,
  ScanResult,
} from '@/features/queue/types';
import { PageContainer } from '@/layouts/PageContainer';
import { isApiError } from '@/lib/api';
import { CounterEkycDrawer } from './CounterEkycDrawer';
import { EkycPhoto } from './EkycPhoto';
import { QueueBoard } from './QueueBoard';
import { ScanResultPanel } from './ScanResultPanel';
import { VerifyCheckInDialog } from './VerifyCheckInDialog';
import { clock } from './queue-format';
import styles from './queue.module.css';

/** Admin v2 "Queue & arrivals": the live queue beside the counter scanner. */
export function QueuePage() {
  const { selected: branch } = useBranch();
  const { hasPermission } = useSession();
  if (!branch) {
    return (
      <PageContainer>
        <p className={styles.warnNote}>
          The queue runs per branch. Choose Bachang or Sungai Udang in the
          branch switch.
        </p>
      </PageContainer>
    );
  }
  return (
    <PageContainer>
      <div className={styles.queueGrid}>
        <QueueBoard title={`Live queue · ${branch.name}`} dayPicker />
        {hasPermission('queue:manage') && <CounterScanner />}
      </div>
    </PageContainer>
  );
}

/** A walk-in after a scan or an IC search: choose the service and payer. */
interface PendingWalkIn {
  patientId: string;
  fullName: string;
}

function CounterScanner() {
  const { t } = useTranslation();
  const a = useQueueActions();
  const { data: services } = useServices();
  const { data: payers } = usePayers();
  const toast = useToast();
  const [token, setToken] = useState('');
  const [ic, setIc] = useState('');
  const [found, setFound] = useState<ArrivalLookup | null>(null);
  const [searching, setSearching] = useState(false);
  const [faceChecked, setFaceChecked] = useState(false);
  const [walkIn, setWalkIn] = useState<PendingWalkIn | null>(null);
  const [service, setService] = useState('');
  const [payer, setPayer] = useState('');
  const [ekycFor, setEkycFor] = useState<ArrivalLookup['patient']>(null);
  const [verifyFor, setVerifyFor] = useState<ArrivalLookup | null>(null);
  const [lastScan, setLastScan] = useState<ScanResult | null>(null);
  const [scanError, setScanError] = useState<unknown>(null);

  const fail = (err: unknown) =>
    toast.show({
      tone: 'error',
      title: isApiError(err) ? (err.problem?.title ?? 'Not done') : 'Not done',
      body: isApiError(err) ? err.message : undefined,
      error: err,
    });

  /** RL-941: the cover chosen in the app no longer pays (self-pay for now). */
  const payerNotice = (n: PayerNotice | null | undefined) =>
    n &&
    toast.show({
      tone: 'error',
      title: 'Who pays: ask the patient',
      body: n.message,
    });

  async function scan() {
    setScanError(null);
    setLastScan(null);
    try {
      const r = await a.scan.mutateAsync(token.trim());
      setLastScan(r);
      if (r.result === 'checked_in') {
        toast.show({
          tone: r.appointment?.arrivedLate ? 'info' : 'success',
          title: `${r.patient.fullName}: Arrived · ticket ${r.appointment?.queueTicket?.number ?? ''}`,
          body: r.message,
        });
      } else {
        setWalkIn({ patientId: r.patient.id, fullName: r.patient.fullName });
        toast.show({
          tone: 'info',
          title: r.patient.fullName,
          body: r.message,
        });
      }
      setToken('');
    } catch (err) {
      setScanError(err);
      fail(err);
    }
  }

  async function search() {
    setSearching(true);
    setFaceChecked(false);
    try {
      setFound(await arrivalLookup(ic.trim()));
    } catch (err) {
      fail(err);
    } finally {
      setSearching(false);
    }
  }

  async function refreshFound() {
    if (ic.trim()) setFound(await arrivalLookup(ic.trim()).catch(() => found));
  }

  const p = found?.patient ?? null;
  const verified = p?.ekyc.status === 'verified';

  return (
    <Card title="Counter scanner">
      <div className={styles.scanner}>
        <p className={styles.infoNote}>
          Point the scanner at the patient’s arrival pass. Identity, booking and
          payer are verified in one step.
        </p>
        <Field label="Scan the arrival pass">
          <Input
            value={token}
            placeholder="Scan, or type the code"
            onChange={(e) => setToken(e.target.value.toUpperCase())}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && token.trim()) void scan();
            }}
          />
        </Field>
        <Button
          variant="primary"
          icon={<ScanLine size={16} />}
          disabled={token.trim().length < 4}
          loading={a.scan.isPending}
          onClick={() => void scan()}
        >
          Scan pass
        </Button>
        <p className={styles.sub}>
          The scanner enters the pass and presses Enter. No scanner, or the scan
          fails? Type the code: the pass in the patient app (OMP1…) or the
          counter code (OMC-…) on the patient’s Details.
        </p>
        <ScanResultPanel
          pending={a.scan.isPending}
          error={scanError}
          result={lastScan}
        />

        <Field label="No phone? Search by IC">
          <Input
            value={ic}
            placeholder="YYMMDD-PB-####"
            onChange={(e) => setIc(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && ic.trim().length >= 5) void search();
            }}
          />
        </Field>
        <Button
          icon={<Search size={16} />}
          disabled={ic.trim().length < 5}
          loading={searching}
          onClick={() => void search()}
        >
          Search
        </Button>

        {found && !p && (
          <p className={styles.warnNote}>
            No record with this IC or passport. Register the patient.
          </p>
        )}
        {p && (
          <div className={styles.section}>
            <div className={styles.headline}>
              <EkycPhoto name={p.fullName} verifiedAt={p.ekyc.verifiedAt} />
              <div>
                <p className={styles.name}>{p.fullName}</p>
                <Badge tone={verified ? 'success' : 'warning'}>
                  {verified ? 'eKYC verified' : 'Unverified'}
                </Badge>
              </div>
            </div>
            {p.inQueue && (
              <p className={styles.warnNote}>
                Already in the queue: number {p.inQueue.number}.
              </p>
            )}
            {!verified && !p.inQueue && (
              <Button
                variant="primary"
                icon={<ShieldCheck size={16} />}
                onClick={() => setVerifyFor(found)}
              >
                {t('queue.verifyCheckIn.action')}
              </Button>
            )}
            {!verified && (
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => setEkycFor(p)}
              >
                Verify only (no check-in)
              </button>
            )}
            {verified && !p.inQueue && (
              <>
                <label className={styles.flagLine}>
                  <input
                    type="checkbox"
                    checked={faceChecked}
                    onChange={(e) => setFaceChecked(e.target.checked)}
                  />
                  Face checked against the eKYC photo
                </label>
                {found!.bookingsToday.map((b) => (
                  <div key={b.id} className={styles.ticketActions}>
                    <span className={styles.sub}>
                      {clock(b.startsAt)} · {b.service.name}
                      {b.doctor ? ` · ${b.doctor.fullName}` : ''}
                    </span>
                    <Button
                      size="sm"
                      variant="primary"
                      disabled={!faceChecked}
                      loading={a.checkInPatient.isPending}
                      onClick={() =>
                        a.checkInPatient
                          .mutateAsync({
                            patientId: p.id,
                            appointmentId: b.id,
                            faceChecked,
                          })
                          .then((r) => {
                            toast.show({
                              tone: r.appointment?.arrivedLate
                                ? 'info'
                                : 'success',
                              title: `Arrived · ticket ${r.appointment?.queueTicket?.number ?? ''}`,
                              body: r.appointment?.arrivedLate
                                ? 'More than 15 minutes late: queued with walk-in priority. Tell the patient now.'
                                : undefined,
                            });
                            payerNotice(r.appointment?.payerNotice);
                            setFound(null);
                            setIc('');
                          })
                          .catch(fail)
                      }
                    >
                      Check in
                    </Button>
                  </div>
                ))}
                {found!.elsewhere.map((b) => (
                  <p key={b.id} className={styles.warnNote}>
                    Booked today at Ommri Klinik {b.branch.name},{' '}
                    {clock(b.startsAt)}.
                  </p>
                ))}
                {found!.bookingsToday.length === 0 && (
                  <Button
                    size="sm"
                    disabled={!faceChecked}
                    onClick={() =>
                      setWalkIn({ patientId: p.id, fullName: p.fullName })
                    }
                  >
                    No booking today: add as walk-in
                  </Button>
                )}
              </>
            )}
          </div>
        )}

        {walkIn && (
          <div className={styles.section} aria-label="Walk-in">
            <p className="eyebrow">Walk-in · {walkIn.fullName}</p>
            <Select
              aria-label="Service"
              value={service}
              onChange={(e) => setService(e.target.value)}
              placeholder="Service"
              options={(services ?? []).map((s) => ({
                value: s.id,
                label: s.name,
              }))}
            />
            <Select
              aria-label="Payment method"
              value={payer}
              onChange={(e) => setPayer(e.target.value)}
              options={[
                { value: '', label: 'Self-pay' },
                ...(payers ?? []).map((x) => ({ value: x.id, label: x.name })),
              ]}
            />
            <div className={styles.ticketActions}>
              <Button
                variant="primary"
                icon={<UserPlus size={16} />}
                disabled={!service}
                loading={a.addWalkIn.isPending}
                onClick={() =>
                  a.addWalkIn
                    .mutateAsync({
                      patientId: walkIn.patientId,
                      serviceId: service,
                      payerId: payer || null,
                    })
                    .then((t) => {
                      toast.show({
                        tone: 'success',
                        title: `Ticket ${t.number} · ${t.patient.fullName}`,
                      });
                      setWalkIn(null);
                      setService('');
                      setPayer('');
                      setFound(null);
                    })
                    .catch(fail)
                }
              >
                Add to walk-in queue
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setWalkIn(null)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
      <VerifyCheckInDialog
        key={verifyFor?.patient?.id ?? 'none-verify'}
        found={verifyFor}
        onClose={() => {
          setVerifyFor(null);
          void refreshFound();
        }}
        onDone={() => {
          setVerifyFor(null);
          setFound(null);
          setIc('');
        }}
      />
      <CounterEkycDrawer
        patient={ekycFor}
        onClose={() => {
          setEkycFor(null);
          void refreshFound();
        }}
      />
    </Card>
  );
}
